import React from 'react';
import {FormGroup, Input, Label} from 'reactstrap';
import {NormalizedColor, HTMLColor} from './types.ts';
import ColorPickerComponent from '../shared/ColorPicker.tsx';
import getColorHexValue from './getColorHexValue.ts';
import OptionalColorPicker from './OptionalColorPicker.tsx';
import useDraft from './useDraft.ts';

interface TextEditorProps {
    text: string;
    color: NormalizedColor | HTMLColor | undefined;
    height: number | undefined;
    billboard: boolean | undefined;
    background: NormalizedColor | HTMLColor | null | undefined;
    onTextChange: (text: string) => void;
    onColorChange: (color: HTMLColor) => void;
    onHeightChange: (height: number | null) => void;
    onBillboardChange: (billboard: boolean) => void;
    onBackgroundChange: (background: HTMLColor | null) => void;
}

// Unset, text is white, half a unit a line, faces the camera and has no
// background. The text saves when the box loses focus: every change rebuilds it.
const TextEditor: React.FC<TextEditorProps> = ({
    text,
    color,
    height,
    billboard,
    background,
    onTextChange,
    onColorChange,
    onHeightChange,
    onBillboardChange,
    onBackgroundChange
}) => {
    const [currentText, setCurrentText] = useDraft(text ?? '');

    const commitText = () => {
        if (currentText !== (text ?? '')) {
            onTextChange(currentText);
        }
    };

    const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        onHeightChange(isNaN(value) ? null : value);
    };

    return (
        <>
            <FormGroup>
                <Label>Text:</Label>
                <Input
                    type="textarea"
                    rows={3}
                    value={currentText}
                    onChange={e => setCurrentText(e.target.value)}
                    onBlur={commitText}
                />
            </FormGroup>
            <FormGroup>
                <Label>Color:</Label>
                <ColorPickerComponent
                    color={getColorHexValue(color ?? '#ffffff')}
                    onChange={onColorChange}
                />
            </FormGroup>
            <FormGroup>
                <Label>Line height:</Label>
                <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={height ?? ''}
                    placeholder="0.5"
                    onChange={handleHeightChange}
                />
            </FormGroup>
            <FormGroup check>
                <Label check>
                    <Input
                        type="checkbox"
                        checked={billboard !== false}
                        onChange={e => onBillboardChange(e.target.checked)}
                    />{' '}
                    Always face the camera
                </Label>
            </FormGroup>
            <FormGroup>
                <Label>Background:</Label>
                <OptionalColorPicker
                    color={background}
                    noneColor="#000000"
                    noneLabel="None (clear)"
                    noneTitle="No background"
                    onChange={onBackgroundChange}
                />
            </FormGroup>
        </>
    );
};

export default TextEditor;
